import React, { useEffect, useMemo, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { FormGroup, Row, Col } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { isEmpty, set, cloneDeep, values, pick } from 'lodash';
import { useTranslation } from 'react-i18next';
import { NewSelectMultiSelect, toOptions } from '../../Components/PIM/common';
import { FilterIcon, ClearIcon, SaveIcon } from '../../bombiix-icon';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import { createOptions, checkArray, getCurrentYearLabel } from '../../Components/helper';
import { localStorageService } from '../../services/localStorage.service';
import { fetchCategoryAction } from '../Dashboard/TodoList/Samples/store/TodoList.action';
import { getCatBasedOnPT, getSubBrandDetails } from '../SystemMasters/Store/SystemMasters.action';
import { filterSortData, clearReducer, getPlaceholderData } from './store/placeholder.actions';
import { usePlaceholderState, LabelOption } from './context/PlaceholderProvider';

type FilterThunkDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

interface RenderOption {
  label: string | number;
  value: string | number;
}

interface FilterOption extends LabelOption {
  isActive?: boolean;
  productType?: Array<{ _id?: string } | string>;
  isUsedCat1?: Array<{ _id?: string } | string>;
  catOneIds?: Array<{ _id?: string } | string>;
  categoryOneId?: string;
  categoryTwoId?: string;
  categoryThreeId?: string;
  brandId?: string;
  year?: number;
}

interface FilterValuesState {
  selectedSeason?: Array<string | number>;
  selectedYear?: Array<string | number>;
  selectedBrand?: Array<string | number>;
  selectedSubBrand?: Array<string | number>;
  selectedCategory1?: Array<string | number>;
  selectedCategory2?: Array<string | number>;
  selectedCategory3?: Array<string | number>;
  selectedCategory4?: Array<string | number>;
  selectedMonth?: Array<string | number>;
  selectedProductType?: Array<string | number>;
}

interface FilterReduxState {
  UserDetails: { userInfo: { userId: string } };
  PlaceholderDetails: { filterValues: FilterValuesState };
}

interface FilterMainState {
  selectedIdsArray: string[];
  [key: string]: unknown;
}

interface FilterPlacementProps {
  setMainState: React.Dispatch<React.SetStateAction<FilterMainState>>;
  subBrandAccess: boolean;
}

type CardDetailsExtended = {
  seasonsId?: Array<string | number>;
  month?: Array<string | number>;
  year?: Array<string | number>;
  brandId?: Array<string | number>;
  subBrandId?: Array<string | number>;
  category1?: Array<string | number>;
  category2?: Array<string | number>;
  category3?: Array<string | number>;
  category4?: Array<string | number>;
  productTypeId?: Array<string | number>;
};

/** Extract the selected ids from a NewSelectMultiSelect (isMulti) change payload. */
const toIds = (selected: unknown): Array<string | number> => (Array.isArray(selected) ? selected.map((item) => (item as RenderOption).value) : []);

/** Map a list of {label,_id} options to react-select {label,value} shape (value = _id). */
const idOptions = (list: FilterOption[] | undefined): RenderOption[] =>
  toOptions(list ?? [], { labelKey: 'label', valueKey: '_id', valueName: 'value' }) as unknown as RenderOption[];

/**
 * Filter popover for the Placeholder Management detail page. Ports the multi-select
 * cascading filters (season/year/month/product type/brand/sub-brand/category 1-4),
 * writing the applied filter to localStorage and reloading placeholder data.
 */
function FilterPlacement({ setMainState, subBrandAccess }: FilterPlacementProps): React.ReactElement {
  const dispatch = useDispatch<FilterThunkDispatch>();
  const { t } = useTranslation('common');
  const {
    UserDetails: {
      userInfo: { userId },
    },
    PlaceholderDetails: {
      filterValues: {
        selectedSeason,
        selectedYear,
        selectedBrand,
        selectedCategory1,
        selectedCategory2,
        selectedCategory3,
        selectedCategory4,
        selectedMonth,
        selectedProductType,
        selectedSubBrand,
      },
    },
  } = useSelector((redState: FilterReduxState) => redState);

  const { placeholderState, setPlaceholderState, cardDetails, placeholderId, individualDetail } = usePlaceholderState();
  const {
    seasonOptions,
    yearOptions,
    brandOptions,
    category1Options,
    category2Options,
    category3Options,
    category4Options,
    monthOptions,
    productTypeOptions,
    pageSize,
    subBrandOptions,
  } = placeholderState;

  const localStorageFilter = localStorageService.get(`placeholderFilter_${placeholderId}`);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);

  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  /** Update a single filter key and cascade-clear dependent categories/months. */
  const handleSelectChange = async (value: Array<string | number>, name: string) => {
    const obj: Record<string, unknown> = {};
    set(obj, `${name}`, value);

    const isCategory1Or2 = ['selectedCategory1', 'selectedCategory2'].some((v) => name.indexOf(v) >= 0);
    const isCategory3 = ['selectedCategory3'].some((v) => name.indexOf(v) >= 0);

    if (isCategory1Or2) {
      obj.selectedCategory3 = [];
      obj.selectedCategory4 = [];
    }
    if (isCategory3) {
      obj.selectedCategory4 = [];
    }

    if (name === 'selectedYear') {
      const remainingYears = value || [];
      const filteredMonths = (selectedMonth || []).filter((monthStr) => {
        const str = String(monthStr || '');
        const match = str.match(/^([a-zA-Z]+)_(\d{4})$/);
        if (!match) return true;
        const yr = Number(match[2]);
        return remainingYears.includes(yr);
      });
      obj.selectedMonth = filteredMonths;
    }

    await dispatch(filterSortData(obj));
  };

  /** Reset all filters and reload data using the card's own season/category/brand/productType scope; month/year go as empty arrays (unfiltered). */
  const clearAll = async () => {
    setPlaceholderState((prevState) => ({
      ...prevState,
      pageIndex: 1,
      placeholderManagement: [],
    }));
    const paramsObj = {
      seasonsId: individualDetail?.season?.map((item) => item._id),
      month: individualDetail?.month,
      year: individualDetail?.year,
      category1: individualDetail?.catOneId?.map((item) => item._id),
      category2: individualDetail?.catTwoId?.map((item) => item._id),
      productTypeId: individualDetail?.productType?.map((item) => item._id),
      brandId: individualDetail?.brand?.map((item) => item._id),
      subBrandId: individualDetail?.subBrand?.map((item) => item._id),
      productType: [],
      pageIndex: 1,
      pageSize,
    };
    const sortinObj = {
      selectedSeason: [],
      selectedYear: [],
      selectedBrand: [],
      selectedCategory1: [],
      selectedCategory2: [],
      selectedCategory3: [],
      selectedCategory4: [],
      selectedMonth: [],
      selectedProductType: [],
    };
    await dispatch(clearReducer());
    localStorageService.remove(`placeholderFilter_${placeholderId}`);
    const res = await dispatch(getPlaceholderData(paramsObj));

    if (res) await dispatch(filterSortData(sortinObj));
  };

  /** Merge selected filters with card defaults, persist to localStorage and reload. */
  const applyFilter = async () => {
    setPlaceholderState((prevState) => ({
      ...prevState,
      pageIndex: 1,
      placeholderManagement: [],
    }));

    const mergeArrays = (selected?: Array<string | number>, individual?: Array<string | number>) => {
      const combined = [...(selected || []), ...(individual || [])];
      return [...new Set(combined)];
    };

    const card = cardDetails as CardDetailsExtended;
    const paramsObj = {
      seasonsId: selectedSeason && selectedSeason.length > 0 ? selectedSeason : mergeArrays(selectedSeason, card?.seasonsId),
      year: selectedYear && selectedYear.length > 0 ? selectedYear : mergeArrays(selectedYear, card?.year),
      brandId: selectedBrand && selectedBrand.length > 0 ? selectedBrand : mergeArrays(selectedBrand, card?.brandId),
      subBrandId: selectedSubBrand && selectedSubBrand.length > 0 ? selectedSubBrand : mergeArrays(selectedSubBrand, card?.subBrandId),
      category1: selectedCategory1 && selectedCategory1.length > 0 ? selectedCategory1 : mergeArrays(selectedCategory1, card?.category1),
      category2: selectedCategory2 && selectedCategory2.length > 0 ? selectedCategory2 : mergeArrays(selectedCategory2, card?.category2),
      category3: selectedCategory3 && selectedCategory3.length > 0 ? selectedCategory3 : mergeArrays(selectedCategory3, card?.category3),
      category4: selectedCategory4 && selectedCategory4.length > 0 ? selectedCategory4 : mergeArrays(selectedCategory4, card?.category4),
      month: selectedMonth && selectedMonth.length > 0 ? selectedMonth : mergeArrays(selectedMonth, card?.month),
      productType: selectedProductType && selectedProductType.length > 0 ? selectedProductType : mergeArrays(selectedProductType, card?.productTypeId),
      loading: true,
      pageSize,
      pageIndex: 1,
    };
    await dispatch(clearReducer());
    localStorageService.set(`placeholderFilter_${placeholderId}`, paramsObj);
    setMainState((prev) => ({
      ...prev,
      selectedIdsArray: [],
    }));

    await dispatch(getPlaceholderData(paramsObj));
  };

  useEffect(() => {
    if (Array.isArray(selectedBrand) && selectedBrand.length > 0) {
      dispatch(
        getSubBrandDetails({
          brandId: selectedBrand,
          loggedInUserId: userId,
        }),
      );
    }
  }, [selectedBrand, dispatch, userId]);

  useEffect(() => {
    const callFn = async () => {
      if (checkArray(selectedCategory1) && checkArray(selectedCategory2) && checkArray(selectedCategory3) && !isEmpty(userId)) {
        const categoryRes = await dispatch(
          fetchCategoryAction({
            categoryType: 4,
            loggedInUserId: userId,
            categoryOneId: selectedCategory1,
            categoryTwoId: selectedCategory2,
            categoryThreeId: selectedCategory3,
            isActive: 1,
          }),
        );
        setPlaceholderState((prevState) => ({
          ...prevState,
          category4Options: cloneDeep(createOptions(categoryRes, 'name', '_id')),
        }));
      } else if (checkArray(selectedCategory1) && checkArray(selectedCategory2) && !isEmpty(userId)) {
        const categoryRes = await dispatch(
          fetchCategoryAction({
            categoryType: 3,
            loggedInUserId: userId,
            categoryOneId: selectedCategory1,
            categoryTwoId: selectedCategory2,
            isActive: 1,
          }),
        );
        setPlaceholderState((prevState) => ({
          ...prevState,
          category3Options: cloneDeep(createOptions(categoryRes, 'name', '_id')),
        }));
      } else {
        setPlaceholderState((prevState) => ({
          ...prevState,
          category3Options: [],
          category4Options: [],
        }));
      }
    };
    callFn();
  }, [selectedCategory1, selectedCategory2, selectedCategory3, dispatch, userId, setPlaceholderState]);

  const monthYearBasedOptions = useMemo<FilterOption[]>(() => {
    const months = (monthOptions ?? []) as FilterOption[];
    const years = (selectedYear ?? []).slice().sort((a, b) => Number(a) - Number(b));
    if (months.length > 0 && years.length > 0) {
      const combined: FilterOption[] = [];
      months.forEach((m) => {
        years.forEach((yr) => {
          const val = m?.value ?? m?._id ?? m?.name;
          const lab = m?.label ?? m?.name ?? String(val);
          combined.push({
            ...m,
            _id: `${val}_${yr}`,
            name: `${val}_${yr}`,
            label: `${lab} ${yr}`,
            value: `${val}_${yr}`,
            year: Number(yr),
          });
        });
      });
      return combined;
    }
    return months;
  }, [selectedYear, monthOptions]);

  const filteredCategory1Options = useMemo<FilterOption[]>(() => {
    const options = (category1Options ?? []) as FilterOption[];
    if (catBasedOnPTData && Array.isArray(selectedProductType) && selectedProductType.length > 0) {
      return options.filter((re) => {
        const rePT = re?.productType;
        const matchesPT = Array.isArray(rePT)
          ? rePT?.some((pt) => selectedProductType.includes((typeof pt === 'object' ? pt?._id : pt) as string | number))
          : true;
        return re?.isActive && matchesPT;
      });
    }
    return options;
  }, [catBasedOnPTData, selectedProductType, category1Options]);

  const filteredCategory2Options = useMemo<FilterOption[]>(() => {
    const options = (category2Options ?? []) as FilterOption[];
    if (Array.isArray(selectedCategory1) && selectedCategory1.length > 0) {
      return options.filter((re) => {
        const usedList = re?.isUsedCat1;
        const linkedList = re?.catOneIds;
        const matchesCat1 =
          (Array.isArray(usedList) && usedList?.some((id) => selectedCategory1.includes((typeof id === 'object' ? id?._id : id) as string | number))) ||
          (Array.isArray(linkedList) && linkedList?.some((id) => selectedCategory1.includes((typeof id === 'object' ? id?._id : id) as string | number)));
        return re?.isActive && (matchesCat1 || (!Array.isArray(usedList) && !Array.isArray(linkedList)));
      });
    }
    return options;
  }, [selectedCategory1, category2Options]);

  const filteredCategory3Options = useMemo<FilterOption[]>(() => {
    const options = (category3Options ?? []) as FilterOption[];
    if (Array.isArray(selectedCategory1) && selectedCategory1.length > 0 && Array.isArray(selectedCategory2) && selectedCategory2.length > 0) {
      return options.filter((opt) => selectedCategory1.includes(opt?.categoryOneId as string) && selectedCategory2.includes(opt?.categoryTwoId as string));
    }
    return options;
  }, [selectedCategory1, selectedCategory2, category3Options]);

  const filteredCategory4Options = useMemo<FilterOption[]>(() => {
    const options = (category4Options ?? []) as FilterOption[];
    if (
      Array.isArray(selectedCategory1) &&
      selectedCategory1.length > 0 &&
      Array.isArray(selectedCategory2) &&
      selectedCategory2.length > 0 &&
      Array.isArray(selectedCategory3) &&
      selectedCategory3.length > 0
    ) {
      return options.filter(
        (opt) =>
          selectedCategory1.includes(opt?.categoryOneId as string) &&
          selectedCategory2.includes(opt?.categoryTwoId as string) &&
          selectedCategory3.includes(opt?.categoryThreeId as string),
      );
    }
    return options;
  }, [selectedCategory1, selectedCategory2, selectedCategory3, category4Options]);

  const seasonRenderOptions = idOptions(seasonOptions as FilterOption[]);
  const productTypeRenderOptions = idOptions(productTypeOptions as FilterOption[]);
  const brandRenderOptions = idOptions(brandOptions as FilterOption[]);
  const category1RenderOptions = idOptions(category1Options as FilterOption[]);
  const category2RenderOptions = idOptions(category2Options as FilterOption[]);
  const category3RenderOptions = idOptions(category3Options as FilterOption[]);
  const category4RenderOptions = idOptions(category4Options as FilterOption[]);

  const yearRenderOptions: RenderOption[] = (yearOptions ?? []).map((option) => ({
    label: option?.label ?? option?.name ?? String(option?._id ?? ''),
    value: (option?.value ?? option?._id) as string | number,
  }));
  const yearRenderValue: RenderOption[] = (yearOptions ?? [])
    .filter((val) => selectedYear?.includes(val?._id))
    .map((option) => ({
      label: option?.label ?? option?.name ?? String(option?._id ?? ''),
      value: (option?.value ?? option?._id) as string | number,
    }));

  const monthRenderOptions: RenderOption[] = monthYearBasedOptions.map((option) => ({
    label: option?.label ?? option?.name ?? String(option?._id ?? ''),
    value: (option?.value ?? option?._id) as string | number,
  }));
  const monthRenderValue: RenderOption[] = monthYearBasedOptions
    .filter((val) => selectedMonth?.includes(val?._id))
    .map((option) => ({
      label: option?.label ?? option?.name ?? String(option?._id ?? ''),
      value: (option?.value ?? option?._id) as string | number,
    }));

  const rawSubBrandOptions =
    Array.isArray(subBrandOptions) && Array.isArray(selectedBrand) && selectedBrand.length > 0
      ? (subBrandOptions as FilterOption[]).filter((item) => item?.isActive === true && selectedBrand.includes(item.brandId as string))
      : [];
  const rawSubBrandValue =
    Array.isArray(subBrandOptions) && Array.isArray(selectedSubBrand)
      ? (subBrandOptions as FilterOption[]).filter((val) => selectedSubBrand.includes(val._id) && selectedBrand?.includes(val.brandId as string))
      : [];

  const objFilter = pick(localStorageFilter, ['seasonsId', 'year', 'brandId', 'category1', 'category2', 'category3', 'category4', 'month', 'productType']);
  const checkFilterBtnActive = !values(objFilter).every(isEmpty);

  return (
    <CustomTooltip placement="top" tooltipText={t('FILTER.FILTER')}>
      <button
        type="button"
        aria-label={t('FILTER.SORT')}
        className={checkFilterBtnActive ? 'bombiix-icon-container active' : 'bombiix-icon-container'}
      >
        <OverlayTrigger
          placement="bottom"
          trigger="click"
          rootClose
          overlay={
            <Popover id="placeholder-filter-popover" className="custom-popover popover-filter">
              <Popover.Content>
                <Row className="form-row">
                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        name="season"
                        containerClassName="product-custom-multiselect common-new-multiselect w-100"
                        options={seasonRenderOptions}
                        value={seasonRenderOptions.filter((val) => selectedSeason?.includes(val.value))}
                        placeholder={t('PLACEHOLDER_MANAGEMENT.SELECT_SEASON')}
                        onChange={(selected) => handleSelectChange(toIds(selected), 'selectedSeason')}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        name="year"
                        containerClassName="product-custom-multiselect common-new-multiselect w-100"
                        options={yearRenderOptions}
                        value={yearRenderValue}
                        placeholder={t('PLACEHOLDER_MANAGEMENT.SELECT_YEAR')}
                        scrollToLabel={getCurrentYearLabel()}
                        onChange={(selected) => handleSelectChange(toIds(selected), 'selectedYear')}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        name="month"
                        containerClassName="product-custom-multiselect common-new-multiselect w-100"
                        options={monthRenderOptions}
                        value={monthRenderValue}
                        placeholder={t('PLACEHOLDER_MANAGEMENT.SELECT_MONTH')}
                        onChange={(selected) => handleSelectChange(toIds(selected), 'selectedMonth')}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        name="productType"
                        containerClassName="product-custom-multiselect common-new-multiselect w-100"
                        options={productTypeRenderOptions}
                        value={productTypeRenderOptions.filter((val) => selectedProductType?.includes(val.value))}
                        placeholder={t('PLACEHOLDER_MANAGEMENT.SELECT_PRODUCT_TYPE')}
                        onChange={(selected) => handleSelectChange(toIds(selected), 'selectedProductType')}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        name="brand"
                        containerClassName="product-custom-multiselect common-new-multiselect w-100"
                        options={brandRenderOptions}
                        value={brandRenderOptions.filter((val) => selectedBrand?.includes(val.value))}
                        placeholder={t('PLACEHOLDER_MANAGEMENT.SELECT_BRAND')}
                        onChange={(selected) => handleSelectChange(toIds(selected), 'selectedBrand')}
                      />
                    </FormGroup>
                  </Col>
                  {subBrandAccess && (
                    <Col md={2}>
                      <FormGroup>
                        <NewSelectMultiSelect
                          isMulti
                          name="subBrand"
                          containerClassName="product-custom-multiselect common-new-multiselect w-100"
                          options={idOptions(rawSubBrandOptions)}
                          value={idOptions(rawSubBrandValue)}
                          placeholder={t('PLACEHOLDER_MANAGEMENT.SELECT_SUB_BRAND')}
                          onChange={(selected) => handleSelectChange(toIds(selected), 'selectedSubBrand')}
                        />
                      </FormGroup>
                    </Col>
                  )}
                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        name="category1"
                        containerClassName="product-custom-multiselect common-new-multiselect w-100"
                        options={idOptions(filteredCategory1Options)}
                        value={category1RenderOptions.filter((val) => selectedCategory1?.includes(val.value))}
                        placeholder={t('PLACEHOLDER_MANAGEMENT.SELECT_CATEGORY_1')}
                        onChange={(selected) => handleSelectChange(toIds(selected), 'selectedCategory1')}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        name="category2"
                        containerClassName="product-custom-multiselect common-new-multiselect w-100"
                        options={idOptions(filteredCategory2Options)}
                        value={category2RenderOptions.filter((val) => selectedCategory2?.includes(val.value))}
                        placeholder={t('PLACEHOLDER_MANAGEMENT.SELECT_CATEGORY_2')}
                        onChange={(selected) => handleSelectChange(toIds(selected), 'selectedCategory2')}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        name="category3"
                        containerClassName="product-custom-multiselect common-new-multiselect w-100"
                        options={idOptions(filteredCategory3Options)}
                        value={category3RenderOptions.filter((val) => selectedCategory3?.includes(val.value))}
                        placeholder={t('PLACEHOLDER_MANAGEMENT.SELECT_CATEGORY3')}
                        onChange={(selected) => handleSelectChange(toIds(selected), 'selectedCategory3')}
                        isDisabled={!(checkArray(selectedCategory1) && checkArray(selectedCategory2))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <NewSelectMultiSelect
                        isMulti
                        name="category4"
                        containerClassName="product-custom-multiselect common-new-multiselect w-100"
                        options={idOptions(filteredCategory4Options)}
                        value={category4RenderOptions.filter((val) => selectedCategory4?.includes(val.value))}
                        placeholder={t('PLACEHOLDER_MANAGEMENT.SELECT_CATEGORY_4')}
                        onChange={(selected) => handleSelectChange(toIds(selected), 'selectedCategory4')}
                        isDisabled={!checkArray(selectedCategory3)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <div className="d-flex justify-content-end">
                      <CustomTooltip placement="top" tooltipText={t('COSTING_TO_DO_LIST.CLEAR')}>
                        <button onClick={clearAll} aria-label="Close" className="btn p-0 d-inline-flex" type="button">
                          <ClearIcon size={28} />
                        </button>
                      </CustomTooltip>
                      <CustomTooltip placement="top" tooltipText={t('COSTING_TO_DO_LIST.APPLY')}>
                        <button onClick={applyFilter} aria-label="Apply" className="btn p-0 d-inline-flex ml-16" type="button">
                          <SaveIcon size={28} />
                        </button>
                      </CustomTooltip>
                    </div>
                  </Col>
                </Row>
              </Popover.Content>
            </Popover>
          }
        >
          <span className="d-inline-flex">
            <FilterIcon size={24} />
          </span>
        </OverlayTrigger>
      </button>
    </CustomTooltip>
  );
}

export default FilterPlacement;
